import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { DsBoxModule, DsFormsModule } from '@bmw-ds/components';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DsButtonModule } from '@bmw-ds/components';
import { DsSkeletonModule } from '@bmw-ds/components';
import { DsLinkModule } from '@bmw-ds/components';
import { CommonService } from 'src/app/services/common.service';
import { DsLoadingAreaConfiguration, DsLoadingAreaService } from '@bmw-ds/components';
import { ApiService } from 'src/app/services/api.service';
import { Router } from '@angular/router';
import { ApiDsMessageComponent } from '../../shared/apiDsMessage/apiDsMessage';
import { DsAccordionModule } from '@bmw-ds/components';
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';

interface DealerModel {
  dealer?: string;
  grpDesc?: string;
}

interface CampaignInventoryModel {
  dealerModel?: DealerModel;
  campaignTypes?: string[];
  lineMakes?: string[];
  enggSeries?: string[];
  modelYears?: string[];
  dealerLocations?: string[];
  vehicleCampaignStatus?: string[];
  error?: string;
}

interface CenterCodeResponse {
  campaignInventoryModel?: CampaignInventoryModel;
  vehicleCampaignStatus?: string[];
  demoLoanerOptions?: string[];
}

@Component({
  selector: 'app-inventory-campaign-details-form',
  standalone: true,
  imports: [DsFormsModule, FormsModule, ReactiveFormsModule, CommonModule, DsBoxModule, DsButtonModule, DsSkeletonModule, DsLinkModule, ApiDsMessageComponent, DsAccordionModule],
  templateUrl: './inventory-campaign-details-form.html',
  styleUrls: ['./inventory-campaign-details-form.scss'],
})
export class InventoryCampaignDetailsForm implements OnInit, OnDestroy {
  centerCodeResponse: CenterCodeResponse | null = null;
  readonly environment = environment;
  dealer: any;
  grpdesc: any;
  private subscriptionsInitialized = false;
  // Suppress downstream filter events during programmatic resets/enables
  private suppressFilterEvents = false;
  CampaignTypeItems: { id: string; label: string }[] = [];
  LineMakeItems: { id: string; label: string }[] = [];
  EngineeringSeriesItems: { id: string; label: string }[] = [];
  ModelYearItems: { id: string; label: string }[] = [];
  LocationItems: { id: string; label: string }[] = [];
  VehicleCampaignStatusItems: { id: string; label: string }[] = [];
  LoanerVehicleCountItems: { id: string; label: string }[] = [];
  isMultiSelected: boolean = false;
  isSingleSelected: boolean = false;
  drpDownResponse: any;
  currentYear: number = 0;
  vcdBackButtonActivated: any;
  selectedCenterCodeFromMarketScreen: any;
  previouslySelectedCriteria: any;
  lastChangedControl: string = '';
  @ViewChild(ApiDsMessageComponent) apiErrorMsg!: ApiDsMessageComponent;
  isPreviouslyNullFilterSelectedField: keyof typeof this.criteria.controls | null = null;
  isNullFilterSelectedFieldSelected: boolean = false;
  onSearchFiltersData: any;
  isNullFieldSelected: boolean = false;
  isClearSelectionClicked: boolean = false;
  isPrevioslyFiltersSelected: boolean = false;
  selectedDropDownKey: string = '';
  isSelectedNotInInventoryDeleted: boolean = false;
  constructor(private commonService: CommonService, private apiService: ApiService, private router: Router, private loadingAreaService: DsLoadingAreaService) { }
  isCenterIDLoading: boolean = false;
  currentDate: string = '';
  currentTime: string = '';
  isFloatingFilterActive: boolean = false;
  activeFilterValues: { colId: string; value: string }[] = [];
  loadingAreaConfig: DsLoadingAreaConfiguration = {
    id: 'bodyLoading',
    loadingTitle: 'Details are being displayed, this may take few seconds..',
    loadingText: 'Please do not close the browser window. wait while we load data.'
  };
  isBackButtonPresent: any;
  // ← ADD: destroy subject to clean up all subscriptions
  private destroy$ = new Subject<void>();
  // ← ADD: dedicated subject to clean up ONLY select subscriptions on each onSearch
  private selectSubscriptions$ = new Subject<void>();
  private userInteractedSinceSearch = false;
  criteria = new FormGroup({
    // Multiselect controls: allow string[] or string values
    CampaignType: new FormControl<string[] | string>([]),
    LineMake: new FormControl<string[] | string>([]),
    EngineeringSeries: new FormControl<string[] | string>([]),
    ModelYear: new FormControl<string[] | string>([]),
    Location: new FormControl<string[] | string>([]),
    // Single-select controls
    VehicleCampaignStatus: new FormControl(''),
    LoanerVehicleCount: new FormControl(''),
  });
  centerIDForm = new FormGroup({
    centerID: new FormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(7), Validators.pattern(/^[0-9]+$/)]),
  });
  ngOnInit(): void {
    this.commonService.updateDateAndTime();
    this.currentDate = this.commonService.currentDate as string;
    this.currentYear = new Date().getFullYear();
    this.currentTime = this.commonService.currentTime + ' EST' as string;
    this.intialFiltersLoad();

    if (this.commonService.isFloatingFilterActive) {
      this.commonService.isFloatingFilterActive
        .pipe(takeUntil(this.destroy$))
        .subscribe((isActive: boolean) => {
          this.isFloatingFilterActive = isActive;
          console.log('Floating filter active in form:', isActive);
        });
    }
    if (this.commonService.agGridFilters) {
      this.commonService.agGridFilters
        .pipe(takeUntil(this.destroy$))
        .subscribe((filters: { colId: string; value: string }[]) => {
          this.activeFilterValues = filters;
          //always block during programmatic resets (onSearch/onClearSelection)
          if (this.suppressFilterEvents) {
            console.log('agGridFilters skipped — suppressFilterEvents is true');
            return;
          }
          //block during onClearSelection explicitly
          if (this.isClearSelectionClicked) {
            console.log('agGridFilters skipped — clear selection in progress');
            return;
          }
          if (this.vcdBackButtonActivated || this.isBackButtonPresent) {
            console.log('agGridFilters skipped — back from VCD, waiting for state restore');
            return;
          }
          const hasActiveFilters = filters.length > 0;
          const hasLastChangedControl = !!this.lastChangedControl && this.lastChangedControl.trim() !== '';
          const hasOnSearchData = !!this.onSearchFiltersData; // ← user did onSearch before

          // ← CASE A: user changed AG Grid floating filter
          // filters has values + no dropdown was changed (lastChangedControl is empty) OR filters has values + dropdown was also changed
          if (hasActiveFilters) {
            this.userInteractedSinceSearch = true;
            console.log('agGridFilters — floating filter changed, calling dropDownFilterSelected');
            const control = hasLastChangedControl ? this.lastChangedControl : (this.onSearchFiltersData ? this.getLastKnownControl() : '');
            if (control) {
              this.dropDownFilterSelected({
                control: control,
                value: this.criteria.get(control)?.value || ''
              });
            } else {
              this.callFilterApiWithCurrentState();
            }
            return;
          }
          // ← CASE B: filters cleared (filters = []) + dropdown was changed
          // e.g. user clears AG Grid filter after having a dropdown selected
          if (!hasActiveFilters && hasLastChangedControl) {
            console.log('agGridFilters — filters cleared, dropdown was changed, calling dropDownFilterSelected');
            if(!this.userInteractedSinceSearch) return;
            this.dropDownFilterSelected({
              control: this.lastChangedControl,
              value: this.criteria.get(this.lastChangedControl)?.value || ''
            });
            return;
          }
          // ← CASE c: filters cleared + NO dropdown changed + onSearch was done
          // user had typed in AG Grid filter, then cleared it — no dropdown touched
          // need to call filter API to restore original onSearch results
          // ← CASE D: only fires when user actually had an AG Grid filter active and then cleared it
          // isFloatingFilterActive guard prevents this from firing during onClearSelection/restore resets
          if (!hasActiveFilters && !hasLastChangedControl && hasOnSearchData) {
            console.log('CASE D: filters cleared + no dropdown changed → restoring onSearch state');
            if(!this.userInteractedSinceSearch) return;
            this.callFilterApiWithCurrentState();
            return;
          }
          // ← CASE d: filters = [] AND lastChangedControl = ''
          // this is initial ngOnInit load or after full reset — skip
          console.log('agGridFilters skipped — initial load or full reset (no filters, no lastChangedControl)');
        });
    }

    this.isBackButtonPresent = this.commonService.isBackButtonPresent.getValue();
    this.vcdBackButtonActivated = this.commonService.vcdBackButtonActivated.getValue();
    console.log('isBackButtonPresent:', this.isBackButtonPresent);
    this.commonService.userInfo$.subscribe((userInfo: any) => {
      // console.log('User Info from Common Service:', userInfo);
      console.log('is B2E and B2D: ', environment.isB2E, environment.isB2D);
      if (environment.isB2D && userInfo?.loginname && !this.isBackButtonPresent && !this.vcdBackButtonActivated) {
        this.apiService.getB2DCenterInfo(userInfo.loginname).subscribe({
          next: (response: any) => {
            const centerId = response?.centerIds[0];
            this.centerIDForm.patchValue({
              centerID: centerId ?? ''
            }, { emitEvent: false });
            this.onSearch();
          },
          error: (error: any) => {
            console.log('Error fetching center info for B2D user:', error);
            this.apiErrorMsg?.showError('Error occurred while fetching data for your account. Please try again.', 'critical');
          },
          complete: () => {
            console.log('Completed fetching center info for B2D user');
          }
        });
    
        return;
      }
    });


    // when the user navigates back from the Market screen with a Center ID in query params
    if (this.isBackButtonPresent) {
      console.log('Case 2: Navigated back from Market screen with Center ID in query params');
      const params = this.router.routerState.snapshot.root.queryParams;
      this.commonService.isFloatingFilterActive.next(false); // Reset floating filter state when coming back from Market screen
      this.commonService.agGridFilters.next([]); // Clear any existing column filters when coming back from Market screen
      const rawValue = params['value'] || '';
      const centerID = rawValue.split('-')[0]?.trim() || '';
      if (centerID.length >= 5) {
        this.centerIDForm.patchValue({
          centerID: centerID
        }, { emitEvent: false });
        this.attachValueChangeSubscriptions();
        this.onSearch();
        return;
      }
    }

    // restoring state after navigating back from VCD screen
    console.log('vcdBackButtonActivated:', this.vcdBackButtonActivated);
    if (this.vcdBackButtonActivated) {
      this.commonService.isFloatingFilterActive.next(false); // Reset floating filter state when coming back from Market screen
      this.commonService.agGridFilters.next([]); // Clear any existing column filters when coming back from Market screen
      this.isSelectedNotInInventoryDeleted = this.commonService.isSelectedNotInInventoryDeleted.getValue();
      const savedState = this.commonService.savedFormState.getValue();
      if (this.isSelectedNotInInventoryDeleted && savedState && savedState.centerID) {
        console.log('Case 4: Returning from VCD after deleting a vehicle that was not in inventory');
        this.centerIDForm.patchValue({
          centerID: savedState?.centerID
        }, { emitEvent: false });
        const restoringloadingAreaConfig = {
          id: 'bodyLoading',
          loadingTitle: 'Reloading the search data, this may take few seconds..',
          loadingText: 'Please do not close the browser window. wait while we load data.'
        };
        this.loadingAreaService.addLoadingArea(restoringloadingAreaConfig);
        this.onSearch(); // Just re-run search to get fresh data without relying on saved state, since the previously selected vehicle is now deleted
        // this.restoreSavedState(savedState); // Restore dropdown selections and disabled states, but rely on fresh API data for options and inventory counts
        return;
      }
      else if (savedState && savedState.centerID && this.isSelectedNotInInventoryDeleted === false) {
        console.log('Case 3: Restoring saved state after VCD back button');
        const restoringloadingAreaConfig = {
          id: 'bodyLoading',
          loadingTitle: 'Restoring your previous selections and filters, this may take few seconds..',
          loadingText: 'Please do not close the browser window. wait while we load data.'
        };
        this.loadingAreaService.addLoadingArea(restoringloadingAreaConfig);
        this.restoreSavedState(savedState);
        return;
      }

    }
    // handle navigation back from national screen
    const savedState = this.commonService.savedFormState.getValue();
    if (savedState?.centerID && !this.isBackButtonPresent && !this.vcdBackButtonActivated) {
      console.log('Case 5: Returning from national screen — restoring center ID from saved state');
      this.commonService.icdResponseData.next('');
      return;
    }

    // when the user manually enters a center code
    console.log('Case 1: Normal load — waiting for user to enter Center ID');
    this.attachValueChangeSubscriptions();
    this.commonService.selectedCriteria.next(this.buildCriteriaFromForm());
    this.commonService.isFloatingFilterActive.next(false); // Reset floating filter state when coming back from Market screen
    this.commonService.agGridFilters.next([]); // Clear any existing column filters when coming back from Market screen

  }

  // ── Attach value change subscriptions (only once) ──────────────
  private attachValueChangeSubscriptions(): void {
    this.criteria.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.commonService.selectedCriteria.next(this.buildCriteriaFromForm());
        this.saveFormState();
      });
    this.centerIDForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.commonService.selectedCriteria.next(this.buildCriteriaFromForm());
        this.saveFormState();
      });
  }

  intialFiltersLoad() {
    this.VehicleCampaignStatusItems = [
      {
        id: 'Vehicles for which all Campaign(s) can be completed',
        label: 'Vehicles for which all Campaign(s) can be completed',
      }];
    this.LoanerVehicleCountItems = [{
      id: `Model Year ${this.currentYear - 3} onwards`,
      label: `Model Year ${this.currentYear - 3} onwards`,
    }];
    this.criteria.controls['VehicleCampaignStatus'].setValue(this.VehicleCampaignStatusItems[0].id, { emitEvent: false });
    this.criteria.controls['LoanerVehicleCount'].setValue(this.LoanerVehicleCountItems[0].id, { emitEvent: false });
    // Disable VehicleCampaignStatus and LoanerVehicleCount before search
    this.criteria.controls['VehicleCampaignStatus'].disable({ emitEvent: false });
    this.criteria.controls['LoanerVehicleCount'].disable({ emitEvent: false });
    this.criteria.patchValue({
      CampaignType: ['ALL'],
      LineMake: ['ALL'],
      EngineeringSeries: ['ALL'],
      ModelYear: ['ALL'],
      Location: ['ALL']
    }, { emitEvent: false });
  }

  // used when user sets AG Grid filter BEFORE changing any dropdown
  private getLastKnownControl(): string {
    if (!this.onSearchFiltersData) return '';
    // ← priority order — check which control has a non-ALL value
    const controls = ['CampaignType', 'LineMake', 'EngineeringSeries', 'ModelYear', 'Location', 'VehicleCampaignStatus', 'LoanerVehicleCount'];
    for (const ctrl of controls) {
      const val = this.criteria.get(ctrl)?.value;
      if (val && Array.isArray(val) && val.length > 0) {
        return ctrl;
      }
      if (val && !Array.isArray(val) && val !== '') {
        return ctrl;
      }
    }
    return 'CampaignType'; // ← fallback to first control
  }

  // ← called when AG Grid filter changes but NO dropdown was ever changed
  private callFilterApiWithCurrentState(): void {
    if (!this.onSearchFiltersData) {
      console.log('callFilterApiWithCurrentState skipped — no onSearchFiltersData');
      return;
    }
    // ← use CampaignType as default control when no dropdown was changed
    const fallbackControl = 'CampaignType';
    this.lastChangedControl = fallbackControl;
    // console.log('callFilterApiWithCurrentState — using fallback control:', fallbackControl);
    this.dropDownFilterSelected({
      control: fallbackControl,
      value: this.criteria.get(fallbackControl)?.value || ''
    });
  }

  // Save current form state to service
  private saveFormState(): void {
    this.commonService.savedFormState.next({
      centerID: this.centerIDForm.get('centerID')?.value ?? null,
      criteria: this.criteria.getRawValue(), // getRawValue includes disabled controls
      lastChangedControl: this.lastChangedControl,
      selectedDropDownPayload: this.selectedDropDownKey ?? '',
      disabledControls: {                     // also save which controls are disabled
        CampaignType: this.criteria.controls['CampaignType'].disabled,
        LineMake: this.criteria.controls['LineMake'].disabled,
        EngineeringSeries: this.criteria.controls['EngineeringSeries'].disabled,
        ModelYear: this.criteria.controls['ModelYear'].disabled,
        Location: this.criteria.controls['Location'].disabled,
        VehicleCampaignStatus: this.criteria.controls['VehicleCampaignStatus'].disabled,
        LoanerVehicleCount: this.criteria.controls['LoanerVehicleCount'].disabled,
      },
      dropdownItems: {
        CampaignTypeItems: this.CampaignTypeItems,
        LineMakeItems: this.LineMakeItems,
        EngineeringSeriesItems: this.EngineeringSeriesItems,
        ModelYearItems: this.ModelYearItems,
        LocationItems: this.LocationItems,
        VehicleCampaignStatusItems: this.VehicleCampaignStatusItems,
        LoanerVehicleCountItems: this.LoanerVehicleCountItems,
      },
      dealer: this.dealer,
      grpdesc: this.grpdesc,
    });
  }



  // Restore form state from saved data
  private restoreSavedState(savedState: any): void {
    // Suppress events during restore to avoid triggering dropDownFilterSelected
    this.suppressFilterEvents = true;
    this.vcdBackButtonActivated = false;
    this.userInteractedSinceSearch = false;
    //cancel ALL stale subscriptions immediately
    this.selectSubscriptions$.next();
    this.subscriptionsInitialized = false;

    // Restore dealer info first (needed for dropDownFilterSelected payload)
    this.dealer = savedState.dealer;
    this.grpdesc = savedState.grpdesc;
    this.commonService.dealerCode.next(this.dealer);
    this.commonService.dealerDesc.next(this.grpdesc);
    // Restore lastChangedControl BEFORE calling dropDownFilterSelected
    this.lastChangedControl = savedState.lastChangedControl || '';
    // Restore dropdown option lists
    this.CampaignTypeItems = savedState.dropdownItems?.CampaignTypeItems || [];
    this.LineMakeItems = savedState.dropdownItems?.LineMakeItems || [];
    this.EngineeringSeriesItems = savedState.dropdownItems?.EngineeringSeriesItems || [];
    this.ModelYearItems = savedState.dropdownItems?.ModelYearItems || [];
    this.LocationItems = savedState.dropdownItems?.LocationItems || [];
    this.VehicleCampaignStatusItems = savedState.dropdownItems?.VehicleCampaignStatusItems || [];
    this.LoanerVehicleCountItems = savedState.dropdownItems?.LoanerVehicleCountItems || [];

    // Restore center ID
    this.centerIDForm.patchValue({
      centerID: savedState.centerID
    }, { emitEvent: false });



    // Restore form values using getRawValue snapshot (includes disabled controls)
    if (savedState.criteria) {
      this.criteria.patchValue(savedState.criteria, { emitEvent: false });
    }

    // Restore disabled/enabled state for each control
    const disabled = savedState.disabledControls;
    if (disabled) {
      const controls = ['CampaignType', 'LineMake', 'EngineeringSeries', 'ModelYear', 'Location', 'VehicleCampaignStatus', 'LoanerVehicleCount'] as const;
      controls.forEach(ctrl => {
        if (disabled[ctrl]) {
          this.criteria.controls[ctrl].disable({ emitEvent: false });
        } else {
          this.criteria.controls[ctrl].enable({ emitEvent: false });
        }
      });
    }

    // Re-attach value change subscriptions
    // this.attachValueChangeSubscriptions();

    // Broadcast restored criteria
    this.commonService.selectedCriteria.next(this.buildCriteriaFromForm());

    // Initialize dropdown subscriptions
    this.initSelectSubscriptions();
    this.onSearchFiltersData = savedState.criteria;

    // Turn off suppress and trigger filter API with the last selected dropdown
    this.suppressFilterEvents = false;
    if (this.lastChangedControl && this.lastChangedControl.trim() !== '') {
      this.dropDownFilterSelected({
        control: savedState.selectedDropDownPayload,
        value: this.criteria.get(this.lastChangedControl)?.value || ''
      });
      return;
    }
    else {
      this.onSearch();
      return;
    }
  }

  onSearch() {
    if (this.centerIDForm.valid) {
      this.isCenterIDLoading = true;
      if (this.isCenterIDLoading) {
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
      }
      this.enablingCriteriaForms();
      this.lastChangedControl = ''; // reset last changed control on new search
      this.onSearchFiltersData = null; // reset onSearchFiltersData on new search
      this.isClearSelectionClicked = true;
      this.commonService.isClearSelectionClicked.next(this.isClearSelectionClicked);
      this.apiService.getCenterInfo({ centerID: this.centerIDForm.value.centerID }).subscribe({
        next: (data: any) => {
          if (data) {
            // Suppress filter events while loading options and selecting defaults
            this.suppressFilterEvents = true;
            this.drpDownResponse = null;
            this.centerCodeResponse = data;
            // console.log('campaignInventoryModel vehicleCampaignStatus:', this.centerCodeResponse?.campaignInventoryModel?.vehicleCampaignStatus);
            // console.log('campaignInventoryModel vehicleCampaignStatus length:', this.centerCodeResponse?.campaignInventoryModel?.vehicleCampaignStatus?.length);
            this.commonService.icdResponseData.next(this.centerCodeResponse);
            this.dealer = this.centerCodeResponse?.campaignInventoryModel?.dealerModel?.dealer;
            this.grpdesc = this.centerCodeResponse?.campaignInventoryModel?.dealerModel?.grpDesc;
            this.commonService.dealerCode.next(this.dealer);
            this.commonService.dealerDesc.next(this.grpdesc);
            this.CampaignTypeItems = this.centerCodeResponse?.campaignInventoryModel?.campaignTypes?.map(
              (item: any) => ({ id: item || '', label: item || '' })
            ) ?? [];
            this.selectAll('CampaignType', this.CampaignTypeItems);
            this.LineMakeItems = this.centerCodeResponse?.campaignInventoryModel?.lineMakes?.map(
              (item: any) => ({ id: item || '', label: item || '' })
            ) ?? [];
            this.selectAll('LineMake', this.LineMakeItems);
            this.EngineeringSeriesItems = this.centerCodeResponse?.campaignInventoryModel?.enggSeries?.map(
              (item: any) => ({ id: item || '', label: item || '' })
            ) ?? [];
            this.selectAll('EngineeringSeries', this.EngineeringSeriesItems);
            this.ModelYearItems = this.centerCodeResponse?.campaignInventoryModel?.modelYears?.map(
              (item: any) => ({ id: item || '', label: item || '' })
            ) ?? [];
            this.selectAll('ModelYear', this.ModelYearItems);
            this.LocationItems = this.centerCodeResponse?.campaignInventoryModel?.dealerLocations?.map(
              (item: any) => ({ id: item || '', label: item || '' })
            ) ?? [];
            this.selectAll('Location', this.LocationItems);
            this.VehicleCampaignStatusItems = (this.centerCodeResponse?.vehicleCampaignStatus ?? []).map(
              (item: string) => ({
                id: item || '',
                label: item || '',
              })
            );
            const vcsFromModel = this.centerCodeResponse?.campaignInventoryModel?.vehicleCampaignStatus;
            const selectedVehicleCampaignStatus = (vcsFromModel && vcsFromModel.length > 1)
              ? (this.VehicleCampaignStatusItems[0]?.id || '')
              : (vcsFromModel?.[0] || this.VehicleCampaignStatusItems[0]?.id || '');
            this.criteria.controls['VehicleCampaignStatus'].setValue(selectedVehicleCampaignStatus, { emitEvent: false });
            this.LoanerVehicleCountItems = (this.centerCodeResponse?.demoLoanerOptions ?? []).map(
              (item: string) => ({
                id: item || '',
                label: item || '',
              })
            );
            this.criteria.controls['LoanerVehicleCount'].setValue(this.LoanerVehicleCountItems[0]?.id || '', { emitEvent: false });
          }
          else {
            this.apiErrorMsg?.showError('No data found for the entered Center ID.', 'critical');
          }
        },
        error: (error: any) => {
          console.log('Error in fetching center info', error);
          this.isClearSelectionClicked = false;
          this.commonService.isClearSelectionClicked.next(this.isClearSelectionClicked);
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id); // Removes the loading area
          this.isCenterIDLoading = false;
          this.vcdBackButtonActivated = false;
          this.apiErrorMsg?.showError('Error occurred while fetching data. Please try again.', 'critical');
        },
        complete: () => {
          this.isCenterIDLoading = false;
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id); // Removes the loading area
          this.criteria.controls['VehicleCampaignStatus'].enable({ emitEvent: false });
          this.criteria.controls['LoanerVehicleCount'].enable({ emitEvent: false });
          //this.apiErrorMsg?.showError('successfully loaded data for the entered Center ID.', 'positive');
          this.commonService.selectedCriteria.next(this.buildCriteriaFromForm());
          this.onSearchFiltersData = this.criteria.getRawValue();
          // console.log('onSearchFiltersData:', this.onSearchFiltersData);
          // Attach subscriptions once
          this.initSelectSubscriptions();
          this.enablingCriteriaForms();
          this.saveFormState();
          if (this.centerCodeResponse?.campaignInventoryModel?.error) {
            this.apiErrorMsg?.showError(this.centerCodeResponse.campaignInventoryModel.error, 'critical');
            this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
            this.criteria.controls['VehicleCampaignStatus'].disable({ emitEvent: false });
            this.criteria.controls['LoanerVehicleCount'].disable({ emitEvent: false });
          }
          this.suppressFilterEvents = false;
          this.userInteractedSinceSearch = false;
          this.isClearSelectionClicked = false;
          this.commonService.isClearSelectionClicked.next(this.isClearSelectionClicked);
          this.isPrevioslyFiltersSelected = false;
          this.previouslySelectedCriteria = null;
          this.vcdBackButtonActivated = false;
          this.isSelectedNotInInventoryDeleted = false;
          this.commonService.isSelectedNotInInventoryDeleted.next(this.isSelectedNotInInventoryDeleted);


        }
      });

    } else {

      console.log('Please enter a valid Center Code before searching.');
    }
  }

  private initSelectSubscriptions() {
    // ← KEY FIX: cancel ALL previous valueChanges subscriptions before attaching new ones
    // this prevents accumulation of subscriptions on each onClearSelection → onSearch
    this.selectSubscriptions$.next();
    this.subscriptionsInitialized = false; //reset flag to allow re-initialization of subscriptions
    if (this.subscriptionsInitialized) return;

    this.subscriptionsInitialized = true;
    this.criteria.controls['CampaignType'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))  // ← auto-unsubscribe on next reset
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'CampaignType';
        this.dropDownFilterSelected({ control: 'CampaignType', value: v });
      });
    this.criteria.controls['LineMake'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'LineMake';
        this.dropDownFilterSelected({ control: 'LineMake', value: v });
      });
    this.criteria.controls['EngineeringSeries'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'EngineeringSeries';
        this.dropDownFilterSelected({ control: 'EngineeringSeries', value: v });
      });
    this.criteria.controls['ModelYear'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'ModelYear';
        this.dropDownFilterSelected({ control: 'ModelYear', value: v });
      });
    this.criteria.controls['Location'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'Location';
        this.dropDownFilterSelected({ control: 'Location', value: v });
      });
    this.criteria.controls['VehicleCampaignStatus'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'VehicleCampaignStatus';
        this.dropDownFilterSelected({ control: 'VehicleCampaignStatus', value: v });
      });
    this.criteria.controls['LoanerVehicleCount'].valueChanges
      .pipe(takeUntil(this.selectSubscriptions$))
      .subscribe(v => {
        if (this.suppressFilterEvents) return;
        this.userInteractedSinceSearch = true;
        this.lastChangedControl = 'LoanerVehicleCount';
        this.dropDownFilterSelected({ control: 'LoanerVehicleCount', value: v });
      });


  }

  dropDownFilterSelected(event: { control: string; value: any }) {
    if (this.suppressFilterEvents) return;
    // this.buildCriteriaFromForm();
    this.selectedDropDownKey = '';
    switch (event.control) {
      case 'CampaignType': this.selectedDropDownKey = 'CampaignType'; break;
      case 'LineMake': this.selectedDropDownKey = 'LineMake'; break;
      case 'EngineeringSeries': this.selectedDropDownKey = 'EngineeringSeries'; break;
      case 'ModelYear': this.selectedDropDownKey = 'ModelYear'; break;
      case 'Location': this.selectedDropDownKey = 'Location'; break;
      default: break;
    }
    if ((event.control === 'VehicleCampaignStatus' || event.control === 'LoanerVehicleCount')) {
      this.isSingleSelected = true;
      this.selectedDropDownKey = this.isPrevioslyFiltersSelected ? this.previouslySelectedCriteria.selectedDropDown : 'CampaignType';
    }
    else {
      this.isMultiSelected = true;
      this.isPrevioslyFiltersSelected = true;
      this.previouslySelectedCriteria = { selectedDropDown: this.selectedDropDownKey };

    }
    // Always read from getRawValue() — includes disabled controls
    const rawValues = this.criteria.getRawValue();

    const drpDwnFiltersPayload = {
      "centerID": this.dealer ?? '',
      "lineMakesSelected": rawValues?.LineMake ?? null,
      "campaignTypesSelected": rawValues?.CampaignType ?? null,
      "enggSeriesSelected": rawValues?.EngineeringSeries ?? null,
      "modelYearsSelected": rawValues?.ModelYear ?? null,
      "locationsSelected": rawValues?.Location ?? null,
      "selectedVehicleCampaignStaus": rawValues?.VehicleCampaignStatus ?? '',
      "selectedDemoLnrOption": rawValues?.LoanerVehicleCount ?? '',
      "selectedDropDown": this.selectedDropDownKey,
      "coloumnFilter": this.isFloatingFilterActive,
      "defectCodeFilter": this.activeFilterValues.find(f => f.colId === "DefectCode")?.value ? true : false,
      "defectDescFilter": this.activeFilterValues.find(f => f.colId === "DefectDescription")?.value ? true : false,
      "bulletinNumberFilter": this.activeFilterValues.find(f => f.colId === "BulletinNumber")?.value ? true : false,
      "recallNumberFilter": this.activeFilterValues.find(f => f.colId === "RecallNumber")?.value ? true : false,
      "defectCodeSelected": this.activeFilterValues.find(f => f.colId === "DefectCode")?.value || '',
      "defectDescSelected": this.activeFilterValues.find(f => f.colId === "DefectDescription")?.value || '',
      "bulletinNumberSelected": this.activeFilterValues.find(f => f.colId === "BulletinNumber")?.value || '',
      "recallNumberSelected": this.activeFilterValues.find(f => f.colId === "RecallNumber")?.value || ''
    };


    // console.log('drpdwnpayload:', drpDwnFiltersPayload);
    this.previouslySelectedCriteria = drpDwnFiltersPayload;
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    // ← suppress BEFORE API — blocks stale valueChanges/agGridFilters during next()/complete()
    this.suppressFilterEvents = true;
    this.apiService.getSelectedFilterData(drpDwnFiltersPayload).subscribe({
      next: (data: any) => {
        if (data) {
          // console.log('Selected Filter Data Response:', data);
          this.commonService.icdResponseData.next(data);
          this.drpDownResponse = data;
          this.applyDropdownDisabledState(data);

        }
      },
      error: (error) => {
        console.error('Error fetching selected filter data:', error);
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id); // Removes the loading area
        this.suppressFilterEvents = false;
      },
      complete: () => {
        try {
          this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
          if (this.isFloatingFilterActive) {
            this.isMultiSelected = false;
          }

          const validControls = ['CampaignType', 'LineMake', 'EngineeringSeries', 'ModelYear', 'Location', 'VehicleCampaignStatus', 'LoanerVehicleCount'];
          const deselectLastChangedControl = this.lastChangedControl as keyof typeof this.criteria.controls;
          const isValidControl = deselectLastChangedControl && validControls.includes(deselectLastChangedControl);

          if (isValidControl) {
            const currentValue = this.criteria.controls[deselectLastChangedControl].getRawValue();
            const isNowNull = currentValue === null || (Array.isArray(currentValue) && currentValue.length === 0);
            const wasNullBefore = this.isPreviouslyNullFilterSelectedField === deselectLastChangedControl;
            if (isNowNull || wasNullBefore) {
              this.updateNullSelectedOptions(deselectLastChangedControl);
            } else if (!this.isFloatingFilterActive) {
              this.updatingFilterOptions();
              this.nullfieldSelectedUpdatingOptinsWithOnSearchData(this.selectedDropDownKey as keyof typeof this.criteria.controls);
              this.selectingTheSelectedOptions();
            }
          } else if (!this.isFloatingFilterActive) {
            // lastChangedControl is empty (floating filter only) — still update options
            this.updatingFilterOptions();
            this.nullfieldSelectedUpdatingOptinsWithOnSearchData(this.selectedDropDownKey as keyof typeof this.criteria.controls);
            this.selectingTheSelectedOptions();
          }

          this.saveFormState();
          this.isMultiSelected = false;
          this.isSingleSelected = false;
          this.vcdBackButtonActivated = false;
          this.commonService.vcdBackButtonActivated.next(this.vcdBackButtonActivated);
          this.commonService.selectedCriteria.next(this.buildCriteriaFromForm());
        } finally {
          this.suppressFilterEvents = false; // ← always runs, even if something throws
        }
      }
    });
  }
  selectingTheSelectedOptions() {
    const suppress = this.suppressFilterEvents;
    this.suppressFilterEvents = true; // ← block valueChanges during patchValue

    if (this.drpDownResponse?.campaignTypesSelected) {
      this.criteria.controls['CampaignType'].setValue(
        this.drpDownResponse.campaignTypesSelected, { emitEvent: false }
      );
    }
    if (this.drpDownResponse?.lineMakesSelected) {
      this.criteria.controls['LineMake'].setValue(
        this.drpDownResponse.lineMakesSelected, { emitEvent: false }
      );
    }
    if (this.drpDownResponse?.enggSeriesSelected) {
      this.criteria.controls['EngineeringSeries'].setValue(
        this.drpDownResponse.enggSeriesSelected, { emitEvent: false }
      );
    }
    if (this.drpDownResponse?.modelYearsSelected) {
      this.criteria.controls['ModelYear'].setValue(
        this.drpDownResponse.modelYearsSelected, { emitEvent: false }
      );
    }
    if (this.drpDownResponse?.locationsSelected) {
      this.criteria.controls['Location'].setValue(
        this.drpDownResponse.locationsSelected, { emitEvent: false }
      );
    }

    this.suppressFilterEvents = suppress; // ← restore previous suppress state
  }

  updateNullSelectedOptions(controlName: keyof typeof this.criteria.controls) {
    const control = this.criteria.controls[controlName];
    this.isNullFieldSelected = true;
    const deselectLastChangedControl = this.lastChangedControl as keyof typeof this.criteria.controls;
    const currentValue = this.criteria.controls[deselectLastChangedControl].getRawValue();
    this.isPreviouslyNullFilterSelectedField = deselectLastChangedControl as keyof typeof this.criteria.controls;
    this.updatingFilterOptions();
    try {
      // CASE 1: User deselected all → value is null or empty array
      const isNowNull = currentValue === null || (Array.isArray(currentValue) && currentValue.length === 0);

      if (isNowNull) {
        console.log(`CASE 1: ${deselectLastChangedControl} deselected all → marking as previously null`);
        // Remember which field was nulled
        this.isPreviouslyNullFilterSelectedField = deselectLastChangedControl;
        this.isNullFieldSelected = true;
        this.nullfieldSelectedUpdatingOptinsWithOnSearchData(deselectLastChangedControl);
        this.unselectingNullField(deselectLastChangedControl);
        this.criteria.controls[deselectLastChangedControl].patchValue('SELECT', { emitEvent: false });
        this.isClearSelectionClicked = true;
        this.commonService.isClearSelectionClicked.next(this.isClearSelectionClicked);
        // CASE 2: User re-selected a value on the previously nulled field
      } else if (this.isPreviouslyNullFilterSelectedField === deselectLastChangedControl && !isNowNull) {
        console.log(`CASE 2: ${deselectLastChangedControl} re-selected after being null → restoring all`);
        // Clear the null tracking flag
        this.isPreviouslyNullFilterSelectedField = null;
        this.isNullFieldSelected = false;
        this.isClearSelectionClicked = false;
        this.commonService.isClearSelectionClicked.next(this.isClearSelectionClicked);
        // Restore previously onSearch dropdowns with fresh API data
        this.updatingFilterOptions();
        this.nullfieldSelectedUpdatingOptinsWithOnSearchData(deselectLastChangedControl);
        this.unselectingNullField(deselectLastChangedControl);
        //  CASE 3: Normal selection change — just update options
      } else {
        console.log(`CASE 3: ${deselectLastChangedControl} normal selection change`);
        this.isNullFieldSelected = false;
        this.saveFormState();
      }
    }
    finally {
      // ← DO NOT set suppressFilterEvents here
      // suppress is managed only by: onSearch complete(), dropDownFilterSelected complete(), restoreSavedState()
      // console.log('updateNullSelectedOptions finally — suppressFilterEvents NOT changed here');
    }

  }
  updatingFilterOptions() {
    if (!this.drpDownResponse?.disableCampaignType) {
      this.CampaignTypeItems = (this.drpDownResponse?.campaignTypesSelected || []).map((item: any) => ({
        id: item || '',
        label: item || ''
      }));
    }
    if (!this.drpDownResponse?.disableLineMake) {
      this.LineMakeItems = (this.drpDownResponse?.lineMakesSelected || []).map((item: any) => ({
        id: item || '',
        label: item || ''
      }));
    }
    if (!this.drpDownResponse?.disableEngineeringSeries) {
      this.EngineeringSeriesItems = (this.drpDownResponse?.enggSeriesSelected || []).map((item: any) => ({
        id: item || '',
        label: item || ''
      }));
    }
    if (!this.drpDownResponse?.disableModelYear) {
      this.ModelYearItems = (this.drpDownResponse?.modelYearsSelected || []).map((item: any) => ({
        id: item || '',
        label: item || ''
      }));
    }
    if (!this.drpDownResponse?.disableLocation) {
      this.LocationItems = (this.drpDownResponse?.locationsSelected || []).map((item: any) => ({
        id: item || '',
        label: item || ''
      }));
    }
    // ← DO NOT set suppressFilterEvents here — it fights with the finally block
    // suppress is managed only by: onSearch complete(), dropDownFilterSelected complete(), restoreSavedState()
  }

  nullfieldSelectedUpdatingOptinsWithOnSearchData(controlName: keyof typeof this.criteria.controls) {
    console.log('Updating dropdown options based on onSearch data for control:', controlName);
    switch (controlName) {
      case 'CampaignType':
        this.CampaignTypeItems = (this.drpDownResponse?.onSearchFilterData || []).map((item: any) => ({
          id: item || '',
          label: item || ''
        }));
        break;
      case 'LineMake':
        this.LineMakeItems = (this.drpDownResponse?.onSearchFilterData || []).map((item: any) => ({
          id: item || '',
          label: item || ''
        }));
        break;
      case 'EngineeringSeries':
        this.EngineeringSeriesItems = (this.drpDownResponse?.onSearchFilterData || []).map((item: any) => ({
          id: item || '',
          label: item || ''
        }));
        break;
      case 'ModelYear':
        this.ModelYearItems = (this.drpDownResponse?.onSearchFilterData || []).map((item: any) => ({
          id: item || '',
          label: item || ''
        }));
        break;
      case 'Location':
        this.LocationItems = (this.drpDownResponse?.onSearchFilterData || []).map((item: any) => ({
          id: item || '',
          label: item || ''
        }));
        break;
      default: break;
    }
  }

  unselectingNullField(deselectLastChangedControl: keyof typeof this.criteria.controls) {
    if (deselectLastChangedControl !== 'CampaignType') this.selectAll('CampaignType', this.CampaignTypeItems);
    if (deselectLastChangedControl !== 'LineMake') this.selectAll('LineMake', this.LineMakeItems);
    if (deselectLastChangedControl !== 'EngineeringSeries') this.selectAll('EngineeringSeries', this.EngineeringSeriesItems);
    if (deselectLastChangedControl !== 'ModelYear') this.selectAll('ModelYear', this.ModelYearItems);
    if (deselectLastChangedControl !== 'Location') this.selectAll('Location', this.LocationItems);
    this.saveFormState();
  }

  applyDropdownDisabledState(drpDownResponse: any): void {
    const controls = this.criteria.controls;
    type CriteriaKey = keyof typeof controls;
    // Helper to disable or enable based on response flag
    const setControl = (controlName: CriteriaKey, shouldDisable: boolean) => {
      if (shouldDisable) {
        controls[controlName].disable({ emitEvent: false });
      } else {
        controls[controlName].enable({ emitEvent: false });
      }
    };

    setControl('CampaignType', drpDownResponse?.disableCampaignType);
    setControl('LineMake', drpDownResponse?.disableLineMake);
    setControl('EngineeringSeries', drpDownResponse?.disableEngineeringSeries);
    setControl('ModelYear', drpDownResponse?.disableModelYear);
    setControl('Location', drpDownResponse?.disableLocation);
  }

  onClearSelection() {
    this.isClearSelectionClicked = true;
    this.commonService.isClearSelectionClicked.next(this.isClearSelectionClicked);
    // Suppress events during reset to avoid duplicate API calls and disables
    this.suppressFilterEvents = true;
    // ← step 2: reset floating filter state
    this.commonService.isClearSelectionClicked.next(true);
    this.commonService.isFloatingFilterActive.next(false);
    this.commonService.agGridFilters.next([]);
    this.isFloatingFilterActive = false;
    this.activeFilterValues = [];
    this.drpDownResponse = null;
    // ← step 3: clear saved form state — but KEEP center ID
    const currentCenterID = this.centerIDForm.get('centerID')?.value ?? '';
    this.commonService.savedFormState.next(null);

    // ← step 4: reset all tracking variables
    this.lastChangedControl = '';
    this.isPreviouslyNullFilterSelectedField = null;
    this.isNullFieldSelected = false;
    this.isMultiSelected = false;
    this.isSingleSelected = false;
    this.isPrevioslyFiltersSelected = false;
    this.previouslySelectedCriteria = null;
    this.drpDownResponse = null;
    this.subscriptionsInitialized = false; // ← allow re-init after fresh search
    // ← step 5: reset dropdowns to initial empty state
    // this.CampaignTypeItems = [];
    // this.LineMakeItems = [];
    // this.EngineeringSeriesItems = [];
    // this.ModelYearItems = [];
    // this.LocationItems = [];

    // ← step 6: reset form to default values — keeps center ID intact
    this.intialFiltersLoad(); // ← resets criteria to ALL + disables VCS & LVC
    // ← step 8: update date/time display
    this.commonService.updateDateAndTime();
    this.currentDate = this.commonService.currentDate as string;
    this.currentTime = this.commonService.currentTime + ' EST' as string;

    // ← step 10: call ONLY onSearch — no dropDownFilterSelected
    // onSearch uses centerID → fetches fresh data → resets all dropdowns

    this.centerIDForm.patchValue({
      centerID: currentCenterID  // ← restore center ID
    }, { emitEvent: false });
    this.onSearch();
    // console.log('Clear selection — calling onSearch with centerID:', currentCenterID);

  }

  onRefresh() {
    // console.log('Refresh clicked');
    this.commonService.savedFormState.next(null);
    this.suppressFilterEvents = true;
    this.commonService.updateDateAndTime();
    this.ngOnInit();
    this.onSearch();
    this.suppressFilterEvents = false;
  }

  enablingCriteriaForms() {
    // Enable controls without emitting valueChanges to avoid triggering handlers
    this.criteria.controls['CampaignType'].enable({ emitEvent: false });//this.drpDownResponse?.disableCampaignType ?? false });
    this.criteria.controls['LineMake'].enable({ emitEvent: false });
    this.criteria.controls['EngineeringSeries'].enable({ emitEvent: false });
    this.criteria.controls['ModelYear'].enable({ emitEvent: false });
    this.criteria.controls['Location'].enable({ emitEvent: false });
  }

  private buildCriteriaFromForm(): Record<string, any> {
    // Use raw values to include disabled controls
    const f = this.criteria.getRawValue() as any;
    const centerId = this.centerIDForm.value.centerID;
    // Format standard fields: ALL by default, Multiple when arrays >1, single value when arrays of 1
    const formatAllOrMultiple = (v: any) => {
      if (v === undefined || v === null || v === '') return "'ALL'";
      if (Array.isArray(v)) {
        if (v.length === 0) return "'ALL'";
        if (v.length === 1) return String(v[0]);
        if (v.length > 1) return 'Multiple';
      }
      return String(v);
    };
    return {
      'Center Code ': centerId ? String(centerId) : '',
      'Line Make': (f?.LineMake),
      'Campaign Type': (f?.CampaignType),
      'Engineering Series': (f?.EngineeringSeries),
      'Model Year': (f?.ModelYear),
      'Location': (f?.Location),
      // For VehicleCampaignStatus and LoanerVehicleCount, reflect exact value (preselected first option, then user selection)
      'Vehicle Campaign Status': String(f?.VehicleCampaignStatus ?? ''),
      'Demo / Loaner Vehicle Count': String(f?.LoanerVehicleCount ?? ''),
    };
  }

  // Select all option ids for a multiselect control
  private selectAll(controlName: 'CampaignType' | 'LineMake' | 'EngineeringSeries' | 'ModelYear' | 'Location', options: { id: string; label: string }[]) {
    const ids = (options || []).map(o => o.id).filter(id => id !== undefined && id !== null && id !== '');
    const ctrl = this.criteria.controls[controlName] as FormControl<string[] | string>;
    ctrl.setValue(ids, { emitEvent: false });
  }

  // UI-only formatter: keeps form values intact, formats display as ALL/Multiple/single
  displaySelection(controlName: 'CampaignType' | 'LineMake' | 'EngineeringSeries' | 'ModelYear' | 'Location') {
    const value = this.criteria.get(controlName)?.value;
    if (value === undefined || value === null || value === '') return 'ALL';
    if (!this.drpDownResponse) return 'ALL';
    if (Array.isArray(value) && value.length === 0) return 'ALL';
    if (controlName === 'CampaignType') {
      return this.drpDownResponse?.selectedCampaignTypeHintIcon;
    }
    if (controlName === 'LineMake') {
      return this.drpDownResponse?.selectedLineMakeHintIcon;
    }
    if (controlName === 'EngineeringSeries') {
      return this.drpDownResponse?.selectedEngineeringSeriesHintIcon;
    }
    if (controlName === 'ModelYear') {
      return this.drpDownResponse?.selectedModelYearHintIcon;
    }
    if (controlName === 'Location') {
      return this.drpDownResponse?.selectedLocationHintIcon;
    }

  }

  // Trigger selection change logic only when the value actually changes
  onSelectionChange(
    controlName: 'CampaignType' | 'LineMake' | 'EngineeringSeries' | 'ModelYear' | 'Location' | 'VehicleCampaignStatus' | 'LoanerVehicleCount',
    event: any
  ) {
    const current = this.criteria.get(controlName)?.value;
    const next = (event && event.value !== undefined) ? event.value : event;
    const arraysEqual = (a: any[], b: any[]) => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    };
    const isEqual = Array.isArray(current) && Array.isArray(next)
      ? arraysEqual(current, next)
      : current === next;
    if (isEqual) return;
    this.dropDownFilterSelected({ control: controlName, value: next });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.selectSubscriptions$.next();
    this.selectSubscriptions$.complete();
  }

}
